import { get } from 'lodash';

import { Form, FormInstance } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`;

export type NestedKeysOf<T> = (T extends object ?
    { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<NestedKeysOf<T[K]>>}` }[Exclude<keyof T, symbol>]
    : '') extends infer D ? Extract<D, string> : never;

export type UseFormInstance<T> = FormInstance<T> & {
	isValid: boolean;
	reset: () => void;
	validate: () => void;
};

export interface UseFormResult<T> {
	Form: typeof Form;
	form: UseFormInstance<T>;
}

export interface UseFormOptions<T> {
	required?: Array<NestedKeysOf<T>>;
}

export function useForm<T>(options?: UseFormOptions<T>): UseFormResult<T> {
	const { required } = options ?? {};
	const [ isValid, setIsValid ] = useState(false);
	const [ form ] = Form.useForm<T>();
	const requiredFields = useRef(required);

	const validate = useCallback(() => {
		setTimeout(() => {
			const values = form.getFieldsValue(true);
			const hasAllValues = Boolean(requiredFields.current?.every((name) => Boolean(get(values, name))));
			const hasErrors = form.getFieldsError().filter((entry) => entry.errors.length).length > 0;

			setIsValid(hasAllValues && !hasErrors);
		});
	}, [ form ]);

	const reset = useCallback(() => {
		form.resetFields();
		setIsValid(false);
	}, [ form ]);

	const formResult = useMemo(
		() => ({
			...form,
			isValid,
			reset,
			validate
		}),
		[ form, isValid, reset, validate ]
	);

	return {
		Form,
		form: formResult
	};
}
